import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Button, { TYPES } from 'components/Button';
import Checkbox from 'components/Checkbox';
import CloseIcon, { SIZES } from 'components/CloseIcon';
import { DrawerPanel, DrawerHeader, DrawerBody, DrawerFooter } from 'components/Drawer';
import GiftMessagePreview from 'components/GiftMessagePreview';
import GiftMessageRemainingCount from 'components/GiftMessageRemainingCount';
import { TertiaryHeading } from 'components/Headings';
import IconButton from 'components/IconButton';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Rudderstack from 'utils/Rudderstack';

// Note, in the current implementation of the gift message preview we need this specified max-width.
const CARD_MAX_WIDTH = 315;

const CartGiftMessagingDrawerPanel = class extends Component {
  static propTypes = {
    visible: PropTypes.bool,
    giftMessage: PropTypes.string,
    viewOverview: PropTypes.func,
    setGiftingOptions: PropTypes.func,
    useLogoFreePackaging: PropTypes.bool,
    isLogoFreePackagingAvailable: PropTypes.bool,
    isGiftMessagingOn: PropTypes.bool,
  };

  static defaultProps = {
    giftMessage: '',
    setGiftingOptions: () => {},
    viewOverview: () => {},
    visible: false,
    useLogoFreePackaging: false,
    isLogoFreePackagingAvailable: false,
    isGiftMessagingOn: false,
  };

  constructor(props) {
    super(props);

    this.updateMessage = this.updateMessage.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);

    this.state = {
      message: props.giftMessage,
      useLogoFreePackaging: props.useLogoFreePackaging,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      (prevProps.giftMessage !== this.props.giftMessage ||
        prevProps.useLogoFreePackaging !== this.props.useLogoFreePackaging) &&
      !this.props.visible
    ) {
      this.setState({
        message: this.props.giftMessage,
        useLogoFreePackaging: this.props.useLogoFreePackaging,
      }); // eslint-disable-line
    }
  }

  updateMessage(message) {
    this.setState({ message });
  }

  updateLogoFreePackagingState(useLogoFreePackaging) {
    this.setState({ useLogoFreePackaging });
  }

  handleSave(e) {
    e.preventDefault();
    this.props.setGiftingOptions({
      message: this.state.message,
      logoFreePackaging: this.state.useLogoFreePackaging,
    });
    this.props.viewOverview();
    Rudderstack.giftMessageSubmitted(this.state.useLogoFreePackaging);
  }

  handleCancel(e) {
    e.preventDefault();
    this.setState(
      { message: this.props.giftMessage, useLogoFreePackaging: this.props.useLogoFreePackaging },
      () => {
        this.props.viewOverview();
      }
    );
  }

  render() {
    const {
      visible,
      viewOverview,
      giftMessage,
      useLogoFreePackaging,
      isLogoFreePackagingAvailable,
      isGiftMessagingOn,
    } = this.props;

    return (
      <DrawerPanel position="right" visible={visible}>
        <DrawerHeader>
          <div className="CartGiftMessagingDrawerPanel__header">
            <div className="CartGiftMessagingDrawerPanel__padded-row">
              <div className="CartGiftMessagingDrawerPanel__close-button">
                <IconButton onClick={viewOverview} aria-label={I18n.localize('cart.closeDrawer')}>
                  <CloseIcon size={SIZES.SMALL} />
                </IconButton>
              </div>
              <p className="CartGiftMessagingDrawerPanel__heading">
                {isGiftMessagingOn
                  ? I18n.localize('cart.gifting.header')
                  : I18n.localize('cart.giftMessagingPrompt.part1').replace('?', '')}
              </p>
            </div>
          </div>
          <hr className="CartGiftMessagingDrawerPanel__horizontal-rule" />
        </DrawerHeader>
        <DrawerBody>
          {isGiftMessagingOn && (
            <div className="CartGiftMessagingDrawerPanel__body">
              <div className="CartGiftMessagingDrawerPanel__body-heading">
                <TertiaryHeading>{I18n.localize('cart.gifting.messageHeader')}</TertiaryHeading>
              </div>
              <GiftMessagePreview
                updateMessage={this.updateMessage}
                giftMessage={this.state.message}
                transitionDuration={Theme.get('transition-duration')}
              />
              <GiftMessageRemainingCount currentCount={this.state.message.length} />
            </div>
          )}
          {isLogoFreePackagingAvailable && (
            <>
              <hr className="CartGiftMessagingDrawerPanel__horizontal-rule" />
              <div className="CartGiftMessagingDrawerPanel__body">
                <div className="CartGiftMessagingDrawerPanel__body-heading">
                  <TertiaryHeading>{I18n.localize('cart.gifting.logoFreeHeader')}</TertiaryHeading>
                </div>
                <Checkbox
                  className="CollectionFilterSelector__checkbox"
                  ariaLabel={I18n.localize(`cart.gifting.logoFreeCheckbox`)}
                  checked={this.state.useLogoFreePackaging}
                  onChange={() =>
                    this.updateLogoFreePackagingState(!this.state.useLogoFreePackaging)
                  }
                >
                  <div className="CollectionFilterSelector__checkbox-label">
                    {I18n.localize('cart.gifting.logoFreeDescription')}
                  </div>
                </Checkbox>
              </div>
            </>
          )}
        </DrawerBody>
        <DrawerFooter>
          <hr className="CartGiftMessagingDrawerPanel__horizontal-rule" />
          <div className="CartGiftMessagingDrawerPanel__footer">
            <div className="CartGiftMessagingDrawerPanel__padded-row">
              <div className="CartGiftMessagingDrawerPanel__footer-flex">
                <div className="CartGiftMessagingDrawerPanel__footer-flex-child">
                  <Button
                    type={TYPES.DEFAULT}
                    isDisabled={
                      this.state.message === giftMessage &&
                      this.state.useLogoFreePackaging === useLogoFreePackaging
                    }
                    onClick={this.handleSave}
                    isFullWidth
                    isUppercase
                  >
                    {I18n.localize('cart.save')}
                  </Button>
                </div>

                <div className="CartGiftMessagingDrawerPanel__footer-flex-child">
                  <Button type={TYPES.OUTLINE} onClick={this.handleCancel} isFullWidth isUppercase>
                    {I18n.localize('cart.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DrawerFooter>
        <style jsx>
          {`
            .CartGiftMessagingDrawerPanel__header {
              padding: ${Theme.get('spacing') * 4}px 0;
            }

            .CartGiftMessagingDrawerPanel__heading {
              margin: 0;
              font-weight: ${Theme.get('font-weight-semi-bold')};
              line-height: 36px;
            }

            .CartGiftMessagingDrawerPanel__padded-row {
              position: relative;
              padding: 0 ${Theme.get('spacing') * 4}px;
            }

            .CartGiftMessagingDrawerPanel__close-button {
              position: absolute;
              right: ${Theme.get('spacing') * 4}px;
            }

            .CartGiftMessagingDrawerPanel__footer-flex {
              display: flex;
              justify-content: space-between;
              width: 100%;
            }

            .CartGiftMessagingDrawerPanel__footer-flex-child {
              width: 50%;
            }

            .CartGiftMessagingDrawerPanel__footer-flex-child:first-child {
              margin-right: ${Theme.get('spacing') * 4}px;
            }

            .CartGiftMessagingDrawerPanel__horizontal-rule {
              border: none;
              height: 1px;
              margin: 0;
              background-color: ${Theme.get('colors-black-20')};
            }

            .CartGiftMessagingDrawerPanel__body {
              padding: ${Theme.get('spacing') * 5}px 0;
              max-width: ${CARD_MAX_WIDTH}px;
              margin: 0 auto;
            }

            .CartGiftMessagingDrawerPanel__body-heading {
              margin-bottom: ${Theme.get('spacing') * 3}px;
            }

            .CartGiftMessagingDrawerPanel__footer {
              padding: ${Theme.get('spacing') * 4}px 0;
            }

            .CollectionFilterSelector__checkbox-label {
              margin-left: ${Theme.get('spacing') * 2}px;
              line-height: 1.38; //this is due to the difference in "Label" vs "label" that we use in <Checkbox />
            }
          `}
        </style>
      </DrawerPanel>
    );
  }
};

export default CartGiftMessagingDrawerPanel;
